// Le MOSSE che un posto puo fare e le COMBINAZIONI della sua mano, per proporle a chi gioca. ⛔ Non decidono niente: la legalita la decide il game host (`puòAgire` ricostruisce
// l'azione dalle SUE mosse legali), e un rifiuto arriva col suo codice. Qui servono solo a mostrare i pulsanti giusti («bussa» solo quando si puo, «gin» solo a deadwood 0) e i
// gruppi col deadwood, invece di far indovinare.
//
// COPIA DICHIARATA da Qlash-IT/QGinRummy @ abe61a3a3cab07f06f2e51a8d4eeb1e287029374 (main, 2026-10-20; la TMA non importa QGinRummy): le stesse righe, con i tipi della TMA.
// Per funzione, il file d'origine (e il suo blob a quello sha):
//   · `seme`, `rango`, `valore` — packages/regole/src/carte.ts (blob 2ff37c27ceb188e56db36fb6ef31c3992db38ada)
//   · `maschere`, `pesoDi`, `deadwood`, `disposizioniOttime` — packages/regole/src/combinazioni.ts (blob a6852591955cf163ddc44539ca881d2e6daa7b74); `perIndice` e la stessa riga
//   · `puoBussare`, `eScartabile`, `con`, `senza`, `bussabile`, `mosse` — packages/regole/src/regole.ts (blob e8209e45b254e36b742298744f3fd114707369d6)
// NON si copiano `attacca`, `difendi` e il resto del motore: il layoff e la disposizione di fine mano le calcola il server e le rende nel `Riepilogo` (`lib/ginrummy/protocol.ts`).
// Differenze dalla fonte: i tipi (`Carta`, `Vista`, `Mossa`, `Gruppo` sono quelli di `./protocol`; `Disposizione` sta qui, nella fonte e in combinazioni.ts), gli import, gli apici
// (singoli, come il resto della TMA) e le virgolette dei commenti senza accenti. Nessun'altra riga cambia: `tests/ginrummy-regole.test.ts` le confronta con casi dei test di QGinRummy.
import type { Carta, Gruppo, Mossa, Vista } from './protocol';

// ─── packages/regole/src/carte.ts ───────────────────────────────────────────────────────────────────────────────────────────────────

export const seme = (c: Carta): number => Math.floor(c / 13);
export const rango = (c: Carta): number => (c % 13) + 1;

/** I punti di una carta spaiata: A = 1, da 2 a 10 il numero, J Q K = 10. */
export const valore = (c: Carta): number => Math.min(rango(c), 10);

// ─── packages/regole/src/combinazioni.ts ────────────────────────────────────────────────────────────────────────────────────────────

/** Una mano disposta: le combinazioni, le carte che restano fuori e il loro peso. */
export interface Disposizione {
  gruppi: Gruppo[];
  spaiate: Carta[];
  deadwood: number;
}

const ULTIMO_RANGO = 13;

/** Le combinazioni possibili in `carte`, come maschere di bit sulle POSIZIONI delle carte (la mano ha al piu 11 carte). */
function maschere(carte: readonly Carta[]): number[] {
  const out: number[] = [];
  // i set: per rango, 3 o 4 carte
  for (let r = 1; r <= ULTIMO_RANGO; r++) {
    const posti: number[] = [];
    carte.forEach((c, i) => {
      if (rango(c) === r) posti.push(i);
    });
    if (posti.length < 3) continue;
    out.push(posti.reduce((m, i) => m | (1 << i), 0));
    if (posti.length === 4) for (const fuori of posti) out.push(posti.reduce((m, i) => (i === fuori ? m : m | (1 << i)), 0));
  }
  // le scale: per seme, ogni tratto di rangi consecutivi lungo almeno 3
  for (let s = 0; s < 4; s++) {
    const perRango: number[] = new Array<number>(ULTIMO_RANGO + 1).fill(-1);
    carte.forEach((c, i) => {
      if (seme(c) === s) perRango[rango(c)] = i;
    });
    for (let inizio = 1; inizio <= ULTIMO_RANGO; inizio++) {
      let m = 0;
      for (let fine = inizio; fine <= ULTIMO_RANGO && perRango[fine] !== -1; fine++) {
        m |= 1 << perRango[fine]!;
        if (fine - inizio >= 2) out.push(m);
      }
    }
  }
  return out;
}

const pesoDi = (carte: readonly Carta[], m: number): number => carte.reduce((t, c, i) => (m & (1 << i) ? t + valore(c) : t), 0);

/** Il deadwood della mano: i punti delle carte fuori dalla disposizione migliore. */
export function deadwood(carte: readonly Carta[]): number {
  const totale = carte.reduce((t, c) => t + valore(c), 0);
  if (carte.length < 3) return totale;
  const gruppi = maschere(carte);
  if (gruppi.length === 0) return totale;
  const pesi = gruppi.map((m) => pesoDi(carte, m));
  let meglio = 0;
  const vai = (da: number, usate: number, acc: number): void => {
    if (acc > meglio) meglio = acc;
    for (let j = da; j < gruppi.length && meglio < totale; j++) {
      if ((gruppi[j]! & usate) === 0) vai(j + 1, usate | gruppi[j]!, acc + pesi[j]!);
    }
  };
  vai(0, 0, 0);
  return totale - meglio;
}

const perIndice = (a: Carta, b: Carta): number => a - b;

/** Tutte le disposizioni col deadwood minimo (una sola se la mano non ha ambiguita), in un ordine che dipende solo dalle carte. */
export function disposizioniOttime(carte: readonly Carta[]): Disposizione[] {
  const ordinate = [...carte].sort(perIndice);
  const totale = ordinate.reduce((t, c) => t + valore(c), 0);
  const gruppi = maschere(ordinate);
  const pesi = gruppi.map((m) => pesoDi(ordinate, m));
  let meglio = 0;
  const trovate: number[][] = [];
  const vai = (da: number, usate: number, acc: number, scelti: number[]): void => {
    if (acc > meglio) {
      meglio = acc;
      trovate.length = 0;
    }
    if (acc === meglio) trovate.push([...scelti]);
    for (let j = da; j < gruppi.length; j++) {
      if ((gruppi[j]! & usate) === 0) {
        scelti.push(j);
        vai(j + 1, usate | gruppi[j]!, acc + pesi[j]!, scelti);
        scelti.pop();
      }
    }
  };
  vai(0, 0, 0, []);
  return trovate.map((scelti) => {
    const lista = scelti.map((j) => ordinate.filter((_, i) => gruppi[j]! & (1 << i)));
    const dentro = new Set(lista.flat());
    const spaiate = ordinate.filter((c) => !dentro.has(c));
    return { gruppi: lista.sort((a, b) => a[0]! - b[0]!), spaiate, deadwood: totale - meglio };
  });
}

// ─── packages/regole/src/regole.ts ──────────────────────────────────────────────────────────────────────────────────────────────────

const con = (mano: readonly Carta[], c: Carta): Carta[] => [...mano, c].sort(perIndice);
const senza = (mano: readonly Carta[], c: Carta): Carta[] => mano.filter((x) => x !== c);

/** Si puo bussare con questo deadwood (le carte restate dopo lo scarto)? */
const puoBussare = (dw: number, soglia: number): boolean => dw <= soglia;

/** La carta si puo scartare? Non quella che il posto di turno ha appena preso dagli scarti. */
const eScartabile = (carta: Carta, presaScarti: Carta | null): boolean => carta !== presaScarti;

/** Il posto di turno puo bussare dopo aver scartato `carta` dalle `undici` carte? */
const bussabile = (undici: readonly Carta[], carta: Carta, soglia: number, dwUndici: number): boolean =>
  // scartare una carta toglie al piu il suo valore dal deadwood: se anche cosi si resta sopra la soglia, e inutile calcolare
  dwUndici - valore(carta) <= soglia && puoBussare(deadwood(senza(undici, carta)), soglia);

/** Le mosse legali del posto che vede: [] se non tocca a lui o la partita e finita. */
export function mosse(v: Vista): Mossa[] {
  const posto = v.posto;
  if (v.turno !== posto || v.fase === 'da-distribuire' || v.fase === 'finita') return [];
  const cima = v.scarti[v.scarti.length - 1];
  switch (v.fase) {
    case 'offerta':
      return [{ t: 'PASSA', posto }, { t: 'PESCA_SCARTI', posto }];
    case 'pesca': {
      const out: Mossa[] = [];
      if (v.carteNelTallone > 0) out.push({ t: 'PESCA_TALLONE', posto });
      if (!v.soloTallone && cima !== undefined) out.push({ t: 'PESCA_SCARTI', posto });
      return out;
    }
    case 'scarto': {
      const dw = deadwood(v.miaMano);
      const carte = v.miaMano.filter((c) => eScartabile(c, v.nonScartare));
      const out: Mossa[] = carte.map((carta) => ({ t: 'SCARTA', posto, carta }));
      for (const carta of carte) if (bussabile(v.miaMano, carta, v.config.soglia, dw)) out.push({ t: 'BUSSA', posto, carta });
      if (dw === 0) out.push({ t: 'BIG_GIN', posto });
      return out;
    }
    case 'ultima': {
      const out: Mossa[] = [{ t: 'PASSA', posto }];
      if (cima === undefined) return out;
      const undici = con(v.miaMano, cima);
      const dw = deadwood(undici);
      for (const carta of undici) if (carta !== cima && bussabile(undici, carta, v.config.soglia, dw)) out.push({ t: 'BUSSA_SCARTI', posto, carta });
      return out;
    }
  }
}
